import { IteratorType } from 'app/infrastructure/ui';
import { ConnectedComponentProps } from 'app/infrastructure/ui/types-uischema';
import { getIteratorChildItems } from 'app/infrastructure/ui/renderer/iterator-resolvers';
import * as resolvers from 'app/infrastructure/ui/renderer/resolvers';
import { CaseState } from 'app/area/case/types-store';
import { getById } from 'app/infrastructure/store/query';

export const sum = values => {
  if (!Array.isArray(values)) {
    return 0;
  }
  return values
    .filter(x => x && x.value)
    .reduce((acc, current) => {
      if (current && current.value) {
        return acc + Number(current.value);
      }
      return current;
    }, 0);
};

const max = values => {
  if (!Array.isArray(values)) {
    return 0;
  }
  return values
    .filter(x => x)
    .reduce((currMax, current) => {
      if (current && current.value && currMax < current.value) {
        return Number(current.value);
      }
      return currMax;
    }, 0);
};

const childValuesAggregate = (
  propertyName: string,
  aggregateColumn: string
) => (
  caseState: CaseState,
  uiEngineProps: ConnectedComponentProps,
  aggregateFunction: (values: any[]) => number
) => {
  // row id's for every row in table matched by entityTypePath, resolveContext should be correct if we are currently iterating
  const rowIds = getIteratorChildItems(
    IteratorType.DataRows,
    caseState,
    uiEngineProps
  );

  // the actual row with values
  const rows = rowIds.map(r =>
    getById(
      (caseState.editing && caseState.editing.values) ||
        caseState.entities[uiEngineProps.hostProps().entityId] ||
        caseState.entities[uiEngineProps.hostProps().entityStoreKey],
      r.storePath
    )
  );

  if (aggregateColumn && rows.length) {
    const valuesForAllRows = rows.map(r =>
      Array.isArray(r[aggregateColumn]) ? r[aggregateColumn][0] : 0
    );
    return {
      [propertyName]: { value: aggregateFunction(valuesForAllRows) }
    };
  }

  return {};
};

export const childValuesTotals = (propertyName: string) => (
  caseState: CaseState,
  uiEngineProps: ConnectedComponentProps
) =>
  childValuesAggregate(propertyName, uiEngineProps.componentDescriptor.dataTag)(
    caseState,
    uiEngineProps,
    sum
  );

export const childValuesMax = (propertyName: string, fieldName: string) => (
  caseState: CaseState,
  uiEngineProps: ConnectedComponentProps
) =>
  childValuesAggregate(propertyName, fieldName)(caseState, uiEngineProps, max);

export const diffFieldValueAndChildValuesTotals = (
  propertyName: string,
  fieldName: string
) => (
  caseState: CaseState,
  uiEngineProps: ConnectedComponentProps
): { [propertyName: string]: { value: number } } => {
  const childValuesAggrSum = childValuesAggregate(
    propertyName,
    uiEngineProps.componentDescriptor.dataTag
  )(caseState, uiEngineProps, sum);
  const fieldValue = resolvers.getComponentValues(
    caseState,
    uiEngineProps,
    fieldName
  );
  let result: number = 0;
  if (fieldValue && childValuesAggrSum[propertyName]) {
    const difference =
      fieldValue && fieldValue.value - childValuesAggrSum[propertyName].value;
    result = difference > 0 ? difference : 0;
  } else {
    result = fieldValue && !!fieldValue.value && fieldValue.value;
  }
  return {
    [propertyName]: { value: result }
  };
};
